import { SupportMessage } from "@/components/layout-loading/support-message";
import { useTeams } from "@/contexts/teams-provider";
import useAccess from "@/hooks/useAccess";
import { useTranslation } from "@/hooks/useTranslation";
import { Layout } from "@/Layout";
import { ReactNode } from "react";
import { Navigate } from "react-router-dom";

/**
 * Sends `/` to the entry point the team's licence calls for. Every seat — basic
 * or professional, desktop or phone — opens on the chat: prospecting now
 * happens there, and the legacy prospecting page is one click away in the menu
 * for the teams that still have it.
 */
export function HomeConditionalRedirect() {
  const { hasAccess } = useAccess();
  const { isBilling, isLoading, teams } = useTeams();

  if (isLoading) {
    return null;
  }

  // The licence check stays: without a seat, /chat bounces back here and the
  // two would redirect to each other.
  if (hasAccess({ requiredLicences: ["basic", "professional"] })) {
    return <Navigate to="/chat" replace />;
  } else if (isBilling) {
    return <Navigate to="/setup/team-creation-billing" replace />;
  }

  if (teams.length === 0) {
    return <Navigate to="/setup/team-creation" replace />;
  }

  // Reaching here means the team itself is healthy — ContentManager intercepts
  // every broken team state before this renders — so the only thing left that
  // can deny every licensed route is the absence of a seat. Licence-gated
  // routes bounce back to this page, so without an explanation the user would
  // face an empty screen.
  return <NoLicenceAssigned />;
}

function NoLicenceAssigned(): ReactNode {
  const { t } = useTranslation();

  return (
    <Layout showNavMain={false} showNavSearch={false} navLinkToTheRoot={false}>
      <div className="flex-col py-6 space-y-3 text-center">
        <h1 className="text-2xl font-bold text-primary">
          {t("setup.noLicenceAssigned.title")}
        </h1>
        <p>
          {t("setup.noLicenceAssigned.line1")}
          <br />
          {t("setup.noLicenceAssigned.line2")}
        </p>
        <SupportMessage />
      </div>
    </Layout>
  );
}
